import type { AdvertisedOffer, PendingCartItem } from "@/modules/anonymousBrowsing";
import { getOrdersCurrentAvailablePromos, postOrdersCurrentPromoCode } from "api/methods";

type ResolvedCode = { code: string; promoCodeId?: string };

/**
 * Claims the special offers a visitor was shown, once their account exists.
 *
 * Replaying the pending cart only re-adds line items, and a special offer is not applied by its items
 * being present: the server builds the cart's discount summary from membership discounts plus promo
 * codes that have actually been posted to the order (`GetPromoDiscountOrderSummary` returns nothing
 * at all when no code is applied). Without this the visitor is quoted a discounted price and charged
 * the full one — and because the flow goes straight to checkout, they also skip the cart page, whose
 * `select-promo-code` guard is what normally stops a patient paying with an offer left unclaimed.
 *
 * Two kinds of advertised offer arrive here. One the visitor claimed through an offer's own call to
 * action, which captured the promo code at the time. The other is a discount merely shown on a
 * product page: `/shop/discounts` carries no promo code, so those arrive with only a discount id and
 * the code has to be resolved against the account that now exists.
 *
 * Sequential and best-effort by design. Sequential because each apply re-prices the whole cart and
 * the server revalidates the applied set on every read, so overlapping writes would race. Best-effort
 * because the verification code is already spent by the time this runs: a rejected offer must leave
 * the patient on a working checkout, and the rejection is then reported honestly by
 * `reconcileAnonymousQuote`, which compares what was advertised against what the cart really applied.
 */
export const applyAdvertisedOffers = async (
  quote: PendingCartItem[],
  claimedOffers: AdvertisedOffer[] = [],
): Promise<void> => {
  const advertised = new Map<string, AdvertisedOffer>();
  for (const item of quote) {
    for (const offer of item.quote?.advertisedDiscounts ?? []) {
      advertised.set(offer.id, offer);
    }
  }
  // Offers claimed with nothing to add sit on the cart rather than an item, but are claimed the same way.
  for (const offer of claimedOffers) {
    advertised.set(offer.id, offer);
  }
  if (advertised.size === 0) return;

  // Keyed by code so one offer covering several items is applied once, and so an offer claimed
  // directly is not applied again after being resolved a second way.
  const codes = new Map<string, ResolvedCode>();
  const unresolved: AdvertisedOffer[] = [];

  for (const offer of advertised.values()) {
    if (offer.promoCodeValue) {
      codes.set(offer.promoCodeValue, {
        code: offer.promoCodeValue,
        promoCodeId: offer.promoCodeId,
      });
    } else {
      unresolved.push(offer);
    }
  }

  if (unresolved.length > 0) {
    // Only answerable now: which promo code carries a given discount depends on the patient, and
    // there was no patient when the price was quoted.
    const available = await getOrdersCurrentAvailablePromos().catch((error: unknown) => {
      console.error("[anonymousCheckout] Could not read available offers:", error);
      return null;
    });

    for (const offer of unresolved) {
      const match = available?.find(
        (promo) => promo.discountId === offer.id && !!promo.promoCode && !promo.isAppliedToCart,
      );
      if (match?.promoCode) {
        codes.set(match.promoCode, {
          code: match.promoCode,
          promoCodeId: match.promoCodeId ?? undefined,
        });
      }
    }
  }

  for (const { code, promoCodeId } of codes.values()) {
    try {
      await postOrdersCurrentPromoCode({ code, ...(promoCodeId ? { promoCodeId } : {}) });
    } catch (error) {
      // Left for reconciliation to report rather than surfaced here: an offer that no longer applies
      // to this account is a normal outcome of signing in, not a checkout failure.
      console.error(`[anonymousCheckout] Could not apply offer ${code}:`, error);
    }
  }
};
